/**
 * The desktop app's half of signing in through the system browser.
 *
 * ## Why the app cannot just show the login screen itself
 *
 * Because an authorization screen inside an application window has no address
 * bar. The person cannot see who they are typing their password into, which is
 * exactly the thing they are told to check, and providers object to the pattern
 * for that reason. So the app hands the whole hop to the real browser and takes
 * a one-time code back afterwards.
 *
 * ## The verifier
 *
 * The code comes home in a URL, and on Windows a URL opened through a scheme
 * arrives as a command-line argument that any local process can read. So the
 * code is deliberately not sufficient: the app invents a secret first, sends
 * only its SHA-256 out, and presents the secret itself when it redeems the
 * code. That is PKCE, applied to our own handoff.
 *
 * ## Why the secret is in localStorage rather than a variable
 *
 * Because the shell answers the incoming URL by navigating its window to the
 * return route, which reloads this application and takes every variable with
 * it. Storage survives that. It is scoped to the app's own origin in the app's
 * own session, so the browser doing the signing in cannot read it - and the
 * threat this defends against is a process reading an argument list, not script
 * running on our own page.
 */
const VERIFIER_KEY = 'gv:desktop-verifier';

/**
 * Start a handoff: invent a secret, keep it, and return the hash to send out.
 *
 * Returns an empty challenge when the platform has no crypto to do it with, in
 * which case the sign-in is simply not offered - see `ProviderSignIn`. Better
 * no button than a handoff with no secret behind it.
 */
export async function beginHandoff(): Promise<string> {
  const verifier = randomHex(32);

  try {
    window.localStorage.setItem(VERIFIER_KEY, verifier);
  } catch {
    return '';
  }

  return sha256Hex(verifier);
}

/** The secret kept by `beginHandoff`, spent once. */
export function takeVerifier(): string | null {
  try {
    const verifier = window.localStorage.getItem(VERIFIER_KEY);

    window.localStorage.removeItem(VERIFIER_KEY);

    return verifier;
  } catch {
    return null;
  }
}

function randomHex(bytes: number): string {
  const buffer = new Uint8Array(bytes);

  crypto.getRandomValues(buffer);

  return [...buffer].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(value: string): Promise<string> {
  /*
   * `crypto.subtle` is unavailable on insecure origins, which in practice means
   * a development machine reached by IP rather than by localhost. The caller
   * treats an empty string as "cannot do this safely" rather than falling back
   * to something weaker.
   */
  if (typeof crypto.subtle?.digest !== 'function') return '';

  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));

  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
